/* GET TICKETS button, ported from the Starfield button JJ sent (Originkit,
   React + Framer Motion) to plain JS for this theme, and recoloured to DC red.

   Same two states as the original:
     idle   a light travels the border, cut to the stroke so nothing spills
     hover  the face fills with a twinkling grid of red "windows"

   What carried over, and why:
   - The light is positioned by ARC LENGTH around the pill, not by tweening a
     gradient position. The original's note is right: tweening the position makes
     the light cut the corner and sprint along the short ends. Constant speed
     round the perimeter is the whole point.
   - The pixel grid is seeded deterministically, no Math.random, so it is the
     same city on every render and never re-rolls mid-hover.
   - One rAF loop drives both the light and the grid, so there is a single thing
     to pause when the button scrolls away or the tab hides.

   What changed: the pill here is a real 999px radius, so the rounded-rect arc
   maths reduces to two straight runs plus two semicircular caps. No Framer, no
   masks in JS, the CSS carries the band mask. */
(() => {
  const REDUCED = matchMedia('(prefers-reduced-motion: reduce)').matches && !window.__dcForceMotion;

  const RED = '#e62020';
  const LAP_SEC = 5.2;      // one trip round the border
  const CELL = 4;           // pixel grid cell, px
  const DENSITY = 0.5;      // fraction of cells that are bright
  const BRIGHT = 1.0;

  /* Deterministic, so the grid is identical every time it is built. */
  const rnd = (i, salt) => {
    const x = Math.sin((i + 1) * 12.9898 + salt * 78.233) * 43758.5453;
    return x - Math.floor(x);
  };

  /* Point at travel fraction t around a pill, measured by arc length. Starts at
     the middle of the top edge and runs clockwise, matching the original. */
  const pointOnPill = (t, w, h) => {
    const r = Math.min(w, h) / 2;
    const sx = Math.max(0, w - 2 * r);
    const arc = Math.PI * r;                 // each cap is a half circle
    const total = 2 * sx + 2 * arc;
    if (total <= 0) return { x: w / 2, y: h / 2 };
    let d = (((t % 1) + 1) % 1) * total + sx / 2;
    d %= total;
    if (d < sx) return { x: r + d, y: 0 };            // top edge, right half
    d -= sx;
    if (d < arc) {                                     // right cap
      const a = d / r - Math.PI / 2;
      return { x: w - r + r * Math.cos(a), y: r + r * Math.sin(a) };
    }
    d -= arc;
    if (d < sx) return { x: w - r - d, y: h };         // bottom edge
    d -= sx;
    const a = d / r + Math.PI / 2;                     // left cap
    return { x: r + r * Math.cos(a), y: r + r * Math.sin(a) };
  };

  function mount(btn) {
    const ring = btn.querySelector('.dc-hero__cta-ring');
    const light = btn.querySelector('.dc-hero__cta-light');
    const canvas = btn.querySelector('.dc-hero__cta-canvas');
    if (!ring || !light || !canvas) return null;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    let W = 0, H = 0, dpr = 1, cells = [], hovered = false;

    const build = () => {
      const cols = Math.max(1, Math.floor(W / CELL));
      const rows = Math.max(1, Math.floor(H / CELL));
      const offX = (W - cols * CELL) / 2;
      const offY = (H - rows * CELL) / 2;
      cells = [];
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const i = r * cols + c;
          const lit = rnd(i, 1) < DENSITY;
          cells.push({
            cx: offX + c * CELL + CELL / 2,
            cy: offY + r * CELL + CELL / 2,
            base: lit ? 0.5 + rnd(i, 2) * 0.5 : 0.05 + rnd(i, 3) * 0.18,
            speed: 0.6 + rnd(i, 4) * 2.4,
            phase: rnd(i, 5) * Math.PI * 2,
          });
        }
      }
    };

    const resize = () => {
      const r = btn.getBoundingClientRect();
      if (!r.width || !r.height) return;
      W = r.width; H = r.height;
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.max(1, Math.round(W * dpr));
      canvas.height = Math.max(1, Math.round(H * dpr));
      build();
    };
    resize();
    const ro = 'ResizeObserver' in window ? new ResizeObserver(resize) : null;
    if (ro) ro.observe(btn);

    const onEnter = () => { hovered = true; };
    const onLeave = () => { hovered = false; };
    btn.addEventListener('pointerenter', onEnter);
    btn.addEventListener('pointerleave', onLeave);
    btn.addEventListener('focus', onEnter);
    btn.addEventListener('blur', onLeave);

    let visible = true;
    const io = 'IntersectionObserver' in window
      ? new IntersectionObserver((es) => { visible = es.some((x) => x.isIntersecting); }, { threshold: 0.01 })
      : null;
    if (io) io.observe(btn);

    let raf = 0, alive = true;
    const dot = Math.max(1, Math.round(CELL * 0.62));
    const off = dot / 2;

    const frame = () => {
      if (!alive) return;
      raf = requestAnimationFrame(frame);
      if (!visible || document.hidden || !W) return;
      const t = performance.now() / 1000;

      // the travelling light, on the stroke
      const p = pointOnPill((t / LAP_SEC) % 1, W, H);
      light.style.transform = `translate3d(${p.x}px, ${p.y}px, 0)`;

      // the grid, only worth drawing while it is visible
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, W, H);
      if (!hovered) return;
      const cx = W / 2, cy = H / 2;
      const maxD = Math.hypot(cx, cy) || 1;
      ctx.globalCompositeOperation = 'lighter';
      ctx.fillStyle = RED;
      for (let k = 0; k < cells.length; k++) {
        const c = cells[k];
        const tw = 0.55 + 0.45 * Math.sin(t * c.speed + c.phase);
        const d = Math.hypot(c.cx - cx, c.cy - cy) / maxD;
        let a = c.base * tw * (0.55 + 0.45 * (1 - d)) * BRIGHT;
        if (a <= 0.002) continue;
        if (a > 1) a = 1;
        ctx.globalAlpha = a;
        ctx.fillRect(c.cx - off, c.cy - off, dot, dot);
      }
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'source-over';
    };
    raf = requestAnimationFrame(frame);

    return {
      destroy() {
        alive = false;
        cancelAnimationFrame(raf);
        try { ro && ro.disconnect(); } catch (e) {}
        try { io && io.disconnect(); } catch (e) {}
        btn.removeEventListener('pointerenter', onEnter);
        btn.removeEventListener('pointerleave', onLeave);
        btn.removeEventListener('focus', onEnter);
        btn.removeEventListener('blur', onLeave);
      },
    };
  }

  function init() {
    if (REDUCED) return;   // the button still works, it just sits still
    document.querySelectorAll('[data-dc-cta]').forEach((btn) => {
      if (btn.__dcCta) return;
      try { const i = mount(btn); if (i) btn.__dcCta = i; } catch (e) {}
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
